
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/hooks/useProfileData";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { personalInfoSchema, type PersonalInfoFormValues } from "../validation/settingsSchema";

export function usePersonalInfo(
  profile: Profile,
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void,
  onProfileUpdate: (profile: Profile) => void
) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<PersonalInfoFormValues>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      full_name: profile.full_name || "",
      phone: profile.phone || "",
      specialization: profile.specialization || "",
    },
  });

  const handleEdit = () => {
    form.reset({
      full_name: profile.full_name || "",
      phone: profile.phone || "",
      specialization: profile.specialization || "",
    });
    setIsEditing(true);
  };

  const handleSave = async (data: PersonalInfoFormValues) => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Optimistically update the UI
      const optimisticProfile = {
        ...profile,
        ...data
      };
      onProfileUpdate(optimisticProfile);

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          phone: data.phone,
          specialization: data.specialization
        })
        .eq('id', user.id);

      if (error) throw error;

      setIsEditing(false);
      toast({
        title: "Profile updated",
        description: "Your profile information has been successfully updated.",
      });
    } catch (error: any) {
      // Revert optimistic update on error
      onProfileUpdate(profile);
      toast({
        variant: "destructive",
        title: "Error updating profile",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset({
      full_name: profile.full_name || "",
      phone: profile.phone || "",
      specialization: profile.specialization || "",
    });
    setIsEditing(false);
  };

  return {
    isEditing,
    setIsEditing: handleEdit,
    form,
    handleSave,
    handleCancel
  };
}
